import { ArrowRight, Building2, ChefHat, Factory, Hotel, ShoppingBasket, Warehouse } from "lucide-react";
import { Link } from "react-router-dom";
import { industries } from "@/content/siteContent";

const icons = [ChefHat, Hotel, Warehouse, Factory, ShoppingBasket, Building2];

export default function IndustriesPage() {
  return (
    <div data-testid="industries-page">
      <section className="bg-slate-950 py-24 text-white md:py-28" data-testid="industries-hero-section">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-4xl space-y-6">
            <div className="section-kicker text-orange-400" data-testid="industries-hero-kicker">Industries</div>
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="industries-hero-heading">
              One consulting lens, adapted across the realities of food service.
            </h1>
            <p className="text-base text-slate-300 md:text-lg" data-testid="industries-hero-description">
              From restaurants to large-scale hospitality environments, the approach remains disciplined while the solution stays specific to the operation.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 md:py-28" data-testid="industries-grid-section">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {industries.map((industry, index) => {
              const Icon = icons[index];
              return (
                <article
                  key={industry.slug}
                  className="lift-card border border-slate-200 bg-slate-50 p-8"
                  data-testid={`industries-card-${industry.slug}`}
                >
                  <Icon className="h-8 w-8 text-orange-600" />
                  <h2 className="mt-6 text-3xl font-bold tracking-tight text-slate-950" data-testid={`industries-card-title-${industry.slug}`}>
                    {industry.title}
                  </h2>
                  <p className="mt-4 text-base text-slate-600" data-testid={`industries-card-description-${industry.slug}`}>
                    {industry.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24 md:py-28" data-testid="industries-priorities-section">
        <div className="mx-auto grid max-w-7xl gap-6 px-6 md:px-12 lg:grid-cols-3">
          {[
            {
              title: "Operational flow",
              description: "Decisions are evaluated through the practical movement of staff, service, and production.",
            },
            {
              title: "Client confidentiality",
              description: "Sensitive business plans and project details are treated with care from the first conversation.",
            },
            {
              title: "Quality, time, and cost balance",
              description: "Projects stay grounded in standards that support successful delivery instead of avoidable friction.",
            },
          ].map((item) => (
            <article
              key={item.title}
              className="lift-card border border-slate-200 bg-white p-8"
              data-testid={`industries-priority-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <h2 className="text-2xl font-bold text-slate-950" data-testid={`industries-priority-title-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {item.title}
              </h2>
              <p className="mt-4 text-base text-slate-600" data-testid={`industries-priority-description-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {item.description}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-white py-24 md:py-28" data-testid="industries-cta-section">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 border border-slate-200 bg-slate-950 px-8 py-12 text-white md:px-12 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl space-y-4">
            <div className="section-kicker text-orange-400" data-testid="industries-cta-kicker">Need a tailored conversation?</div>
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl" data-testid="industries-cta-heading">
              Let’s talk about your segment, constraints, and next move.
            </h2>
          </div>
          <Link
            className="inline-flex items-center justify-center gap-2 rounded-sm bg-orange-600 px-6 py-4 text-sm font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-orange-500"
            data-testid="industries-cta-link"
            to="/contact"
          >
            Get in touch
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
